import React, { type FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from '@emotion/styled'
import {
  Button,
  LoadingDots,
  Modal,
  NewSelect,
  Text,
  TextArea,
} from '@retailer-platform/shared-components'
import { ProblemType } from '../../../../api'
import { useDomainMessages } from '../../../../utils/domain/intl'
import { type ReportAProblemFormValues } from './ProductsSearchReportAProblem'

interface ReportAProblemModalProps {
  /**
   * The number of products that have been selected
   */
  productCount: number
  /**
   * The values of the fields in this modal
   */
  values: ReportAProblemFormValues
  /**
   * Indicates if the form is submitting.
   */
  isSubmitting: boolean
  /**
   * Notifies when any values inside the modal have changed.
   */
  onChange?: (ReportAProblemModalValues) => void

  /**
   * Notifies when the submit button is pressed, with the newly selected values
   */
  onSubmit?: () => void

  /**
   * Notifies when the close button is pressed
   */
  onClose?: () => void
}

const Container = styled.div({
  display: 'flex',
  flex: 1,
  height: '80%',
  minHeight: '100px',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
})

const InputWrapper = styled.div({
  margin: '1rem 0rem',
  width: '100%',
})

const NoteWrapper = styled.div({
  margin: '4px',
})

// Problem types acceptable for this modal's dropdown
const ACCEPTED_PROBLEM_TYPES = [
  ProblemType.ProblemTypeProductAlcohol,
  ProblemType.ProblemTypeProductBrand,
  ProblemType.ProblemTypeProductCharacteristics,
  ProblemType.ProblemTypeProductDetails,
  ProblemType.ProblemTypeProductImage,
  ProblemType.ProblemTypeOther,
  ProblemType.ProblemTypeProductSize,
  ProblemType.ProblemTypeProductTaxonomy,
]

// The localized strings are based on the problem type values. This makes it easier to request them.
const PROBLEM_TYPE_LOCALE_STRINGS = ACCEPTED_PROBLEM_TYPES.reduce((accumulatedValues, p) => {
  accumulatedValues[p + 'label'] = 'productsDomain.search.reportAProblem.' + p + '.label'
  accumulatedValues[p + 'description'] =
    'productsDomain.search.reportAProblem.' + p + '.description'
  return accumulatedValues
}, {})

const ReportAProblemModal: FunctionComponent<ReportAProblemModalProps> = ({
  productCount,
  values,
  isSubmitting,
  onChange,
  onClose,
  onSubmit,
}) => {
  const { problemType, description } = values

  // Load locale strings.
  const i18n = useDomainMessages({
    ...PROBLEM_TYPE_LOCALE_STRINGS,
    problemTypePlaceholder: 'productsDomain.search.reportAProblem.modal.placeholders.problemType',
    descriptionPlaceholder: 'productsDomain.search.reportAProblem.modal.placeholders.description',
  })

  // Problem type must be provided, and if it is ProblemTypeOther (then description must be provided.
  const submitDisabled =
    !problemType || (problemType === ProblemType.ProblemTypeOther && !description) || isSubmitting

  // Build the dropdown options.
  const options = React.useMemo(
    () =>
      ACCEPTED_PROBLEM_TYPES.map(p => ({
        label: i18n[p + 'label'],
        description: i18n[p + 'description'],
        value: p,
      })),
    [i18n]
  )

  // Create input memoized callbacks
  const problemTypeCallback = React.useCallback(
    value => onChange({ description: description, problemType: value }),
    [description, onChange]
  )
  const descriptionCallback = React.useCallback(
    event => onChange({ problemType: problemType, description: event.target.value }),
    [problemType, onChange]
  )

  const header = (
    <FormattedMessage
      id="productsDomain.search.reportAProblem.modal.title"
      values={{ count: productCount }}
    />
  )
  const body = (
    <Container>
      <InputWrapper>
        <NewSelect
          options={options}
          placeholder={i18n.problemTypePlaceholder}
          value={problemType}
          onChange={problemTypeCallback}
        />
        <NoteWrapper>
          {problemType && <Text size="small">{i18n[problemType + 'description']}</Text>}
        </NoteWrapper>
      </InputWrapper>
      <InputWrapper>
        <TextArea
          placeholder={i18n.descriptionPlaceholder}
          onChange={descriptionCallback}
          value={description || ''}
        />
      </InputWrapper>
    </Container>
  )
  const footer = (
    <Button aria-label={'action.submit'} disabled={submitDisabled} onClick={onSubmit}>
      {isSubmitting ? <LoadingDots /> : <FormattedMessage id="action.submit" />}
    </Button>
  )

  return (
    <Modal
      onRequestClose={onClose}
      isOpen={true}
      headerContent={header}
      bodyContent={body}
      footerContent={footer}
    />
  )
}

export default ReportAProblemModal
